import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Button,
  Stack,
  VStack,
  Icon,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react';
import { FaRegSmileBeam } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';

const SurveyComplete = () => {
  const { endpoint } = useParams();
  const { t } = useTranslation();
  const [closingMessage, setClosingMessage] = useState('');
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    if (!surveySession?.valid) {
      navigate('../');
    }
    if (!surveySession?.group || !surveySession?.role) {
      navigate('../acknowledgment');
    }
    // Set a default closing message from survey_session or translations.
    setClosingMessage(surveySession.closing_message || t('public_survey.complete.defaultMessage'));
  }, [t, navigate]);

  const handleFinishClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = async () => {
    try {
      const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
      const uuid = surveySession?.uuid;
      if (uuid) {
        // Mark the survey as completed.
        await axios.post(`/public_surveys/${endpoint}/complete`, { uuid });
      }
    } catch (error) {
      console.error('Error completing survey:', error);
    } finally {
      setIsModalOpen(false);
      // Clear survey data.
      localStorage.removeItem('survey_session');
      localStorage.removeItem('survey_answers');
      navigate('/');
    }
  };

  const handleReview = () => {
    const surveySession = JSON.parse(localStorage.getItem('survey_session')) || {};
    const totalPages = surveySession.total_pages || 1;
    // Update the session and then navigate to the last question page.
    surveySession.page = totalPages;
    localStorage.setItem('survey_session', JSON.stringify(surveySession));
    navigate(`../questions`);
  };

  return (
    <Box p={{ base: 4, md: 6 }} mx="auto" maxW={{ base: '95%', md: 'lg' }} textAlign="center">
      {/* Hero Section */}
      <VStack spacing={{ base: 4, md: 6 }}>
        <Center>
          <Icon as={FaRegSmileBeam} boxSize={{ base: 10, md: 12 }} color="yellow.400" />
        </Center>
        <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="teal.600">
          {t('public_survey.complete.thankYou')}
        </Text>
        <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
          {t('public_survey.complete.appreciation')}
        </Text>
      </VStack>

      {/* Closing Message */}
      <Box
        mt={{ base: 6, md: 8 }}
        p={{ base: 4, md: 6 }}
        bg="white"
        border="1px"
        borderColor="gray.200"
        borderRadius="md"
        shadow="md"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(closingMessage) }}
      />

      {/* Stack buttons responsively: vertical on mobile, horizontal on desktop */}
      <Stack
        mt={{ base: 6, md: 10 }}
        direction={{ base: 'column', md: 'row' }}
        spacing={{ base: 2, md: 4 }}
        w="100%"
      >
        <Button
          size={{ base: 'md', md: 'lg' }}
          variant="outline"
          colorScheme="green"
          onClick={handleReview}
          p={{base: 2, md: 4}}
          _hover={{ bg: 'white', color: 'teal.600' }}
          flex="1"
          >
          {t('public_survey.questions.back', "Review/Update Answers")}
        </Button>
        <Button
          size={{ base: 'lg', md: 'lg' }}
          variant="outline"
          colorScheme="teal"
          onClick={handleFinishClick}
          p={{base: 2, md: 4}}
          mt={{base: 4, md: 0}}
          _hover={{ bg: 'teal.600', color: 'white' }}
          flex="1"
        >
          {t('public_survey.complete.finishSurvey')}
        </Button>
      </Stack>

      {/* Modal for confirmation */}
      <Modal isOpen={isModalOpen} onClose={handleModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={{ base: 'lg', md: 'xl' }}>{t('public_survey.complete.thankYou')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text my={{ base: 2, md: 4 }}>{t('public_survey.complete.defaultMessage')}</Text>
            <Text mt={{ base: 2, md: 4 }}>
              {t('public_survey.complete.finalConfirmation', "Click OK if you're ready to finish the survey.")}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="teal" mr={3} size={{ base: 'sm', md: 'md' }} onClick={handleModalClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SurveyComplete;